import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type NotificationsList, type PlayerNotification } from '../lib/api';
import { useApp } from '../store';
import { useTranslation } from 'react-i18next';
import { formatDateTimeShort } from '../i18n/format';

const PLAYABLE_ZOOM = 11.2;

/** Alerte déménagement : visible jusqu’à la fin du 9 nov. 2026 (Europe/Paris). */
const SERVER_MOVE_ALERT_UNTIL_MS = Date.parse('2026-11-09T23:59:59+01:00');

function serverMoveAlertActive(nowMs = Date.now()) {
  return Number.isFinite(SERVER_MOVE_ALERT_UNTIL_MS) && nowMs <= SERVER_MOVE_ALERT_UNTIL_MS;
}

function notificationDate(value: string) {
  const date = new Date(value.replace(' ', 'T'));
  return Number.isNaN(date.getTime())
    ? value
    : formatDateTimeShort(date);
}

function iconFor(notification: PlayerNotification) {
  return notification.type === 'territorial_threat' ? '!' : '•';
}

export function NotificationsBell() {
  const { t } = useTranslation(['panels', 'common']);
  const { user, setSelected, setPanel, requestFlyTo } = useApp();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [moveAlertActive, setMoveAlertActive] = useState(() => serverMoveAlertActive());
  const rootRef = useRef<HTMLDivElement>(null);

  const query = useQuery({
    queryKey: ['notifications', user?.id],
    queryFn: () => api.notifications(),
    enabled: !!user,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });

  const markRead = useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ['notifications', user?.id] });
      const previous = qc.getQueryData<NotificationsList>(['notifications', user?.id]);
      qc.setQueryData(['notifications', user?.id], (cur: NotificationsList | undefined) =>
        cur ? { ...cur, unreadCount: 0, notifications: cur.notifications.map((n) => ({ ...n, readAt: n.readAt ?? new Date().toISOString() })) } : cur,
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) qc.setQueryData(['notifications', user?.id], context.previous);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ['notifications', user?.id] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.deleteNotification(id),
    onSuccess: (_data, id) => {
      qc.setQueryData<NotificationsList>(['notifications', user?.id], (cur) =>
        cur ? { ...cur, notifications: cur.notifications.filter((n) => n.id !== id), unreadCount: Math.max(0, cur.unreadCount - (cur.notifications.some((n) => n.id === id && !n.readAt) ? 1 : 0)) } : cur,
      );
    },
  });

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  useEffect(() => {
    const sync = () => setMoveAlertActive(serverMoveAlertActive());
    sync();
    const id = window.setInterval(sync, 60_000);
    return () => window.clearInterval(id);
  }, []);

  const data = query.data;
  if (!user || !data?.enabled) return null;

  const badgeCount = data.unreadCount + (moveAlertActive ? 1 : 0);
  const hasListContent = moveAlertActive || data.notifications.length > 0;

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next && data && data.unreadCount > 0) markRead.mutate();
  }

  function openTerritory(notification: PlayerNotification) {
    if (notification.territoryId == null) return;
    const c = notification.centroid;
    setSelected(notification.territoryId);
    if (c && Number.isFinite(c.lng) && Number.isFinite(c.lat)) {
      requestFlyTo({
        lng: c.lng,
        lat: c.lat,
        zoom: PLAYABLE_ZOOM,
        territoryId: notification.territoryId,
        openTerritory: true,
      });
    }
    setPanel('territory');
    setOpen(false);
  }

  return (
    <div ref={rootRef} className="pointer-events-auto relative">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
        onPointerDown={(e) => e.stopPropagation()}
        aria-label={t('panels:notifications.aria')}
        aria-expanded={open}
        className={`relative z-[1] flex h-9 w-9 items-center justify-center rounded-full border border-[var(--line)] bg-[var(--bg-panel)] text-[var(--ink)] shadow-lg backdrop-blur-md transition hover:border-[var(--accent)] hover:text-[var(--accent)] sm:h-11 sm:w-11 ${
          moveAlertActive ? 'animate-bell-blink' : ''
        }`}
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {badgeCount > 0 && (
          <span className="absolute -right-1.5 -top-1.5 min-w-4 rounded-full bg-[var(--danger)] px-1 text-center text-[9px] font-bold leading-4 text-white">
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </button>

      {open && (
        <section className="absolute left-0 top-full z-20 mt-2 flex max-h-[max(24rem,calc(100vh-4rem))] w-[min(22rem,calc(100vw-1.25rem))] flex-col overflow-hidden rounded-2xl border border-[var(--line)] bg-[var(--bg-panel)] shadow-2xl backdrop-blur-xl">
          <div className="flex items-center justify-between border-b border-[var(--line)] px-4 py-3">
            <div>
              <h2 className="font-semibold">{t('panels:notifications.title')}</h2>
              <p className="text-[10px] text-[var(--muted)]">{t('panels:notifications.subtitle')}</p>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="text-xs text-[var(--muted)] hover:text-[var(--ink)]">{t('common:close')}</button>
          </div>
          <div className="min-h-0 overflow-y-auto">
            {!hasListContent ? (
              <p className="px-4 py-8 text-center text-sm text-[var(--muted)]">{t('panels:notifications.empty')}</p>
            ) : (
              <ul className="divide-y divide-[var(--line)]">
                {moveAlertActive && (
                  <li className="relative bg-[var(--danger)]/10 px-4 py-3">
                    <div className="flex gap-3 text-left">
                      <span className="mt-0.5 text-lg text-[var(--danger)]" aria-hidden="true">!</span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-[var(--ink)]">{t('panels:notifications.serverMoveTitle')}</span>
                        <span className="mt-1 block text-xs leading-relaxed text-[var(--muted)]">{t('panels:notifications.serverMoveMessage')}</span>
                        <span className="mt-1.5 block text-[10px] font-medium text-[var(--danger)]">{t('panels:notifications.serverMoveSticky')}</span>
                      </span>
                    </div>
                  </li>
                )}
                {data.notifications.map((notification) => (
                  <li key={notification.id} className={`relative px-4 py-3 ${notification.readAt ? '' : 'bg-[var(--accent)]/5'}`}>
                    <button
                      type="button"
                      disabled={notification.territoryId == null}
                      onClick={() => openTerritory(notification)}
                      className="flex w-full gap-3 pr-8 text-left transition hover:opacity-90 disabled:cursor-default disabled:hover:opacity-100"
                    >
                      <span className="mt-0.5 text-lg">{iconFor(notification)}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-[var(--ink)]">{notification.title}</span>
                        <span className="mt-1 block text-xs leading-relaxed text-[var(--muted)]">{notification.message}</span>
                        <span className="mt-1.5 flex items-center gap-2 text-[10px] text-[var(--muted)]">
                          <span>{notificationDate(notification.availableAt)}</span>
                          {notification.territoryId != null && (
                            <span className="text-[var(--accent)]">{t('panels:notifications.viewOnMap')}</span>
                          )}
                        </span>
                      </span>
                    </button>
                    <button type="button" aria-label={t('panels:notifications.deleteAria')} disabled={deleteMutation.isPending} onClick={() => deleteMutation.mutate(notification.id)} className="absolute right-3 top-3 text-xs text-[var(--muted)] transition hover:text-[var(--danger)] disabled:opacity-50">×</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
